import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/shared/components/ui/Button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { WizardStepProps } from '../types/wizard.types';
import { ComponentReturnTypeMap, Fact } from '@momentum/types';
import { Textarea } from '@/shared/components/ui/Textarea';

interface SortableFactItemProps {
  fact: Fact;
  onEdit: (id: string, newText: string) => void;
  onDelete: (id: string) => void;
  isEditing: boolean;
  onStartEdit: (id: string) => void;
  onCancelEdit: () => void;
}

const SortableFactItem: React.FC<SortableFactItemProps> = ({
  fact,
  onEdit,
  onDelete,
  isEditing,
  onStartEdit,
  onCancelEdit,
}) => {
  const { t } = useTranslation('wizard');
  const [editText, setEditText] = useState(fact.text);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: fact.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSaveEdit = () => {
    if (editText.trim()) {
      onEdit(fact.id, editText.trim());
    } else {
      onCancelEdit();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.altKey || e.metaKey)) {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      onCancelEdit();
    }
  };

  useEffect(() => {
    if (isEditing) {
      setEditText(fact.text);
    }
  }, [isEditing, fact.text]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border rounded-lg p-3 shadow-sm  overflow-wrap-anywhere break-all ${
        isDragging ? 'shadow-lg' : ''
      }`}
    >
      {isEditing ? (
        <div className="space-y-2">
          <Textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter fact..."
            className="w-full"
            autoFocus
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSaveEdit}>
              {t('common.buttons.save')}
            </Button>
            <Button size="sm" variant="outline" onClick={onCancelEdit}>
              {t('common.buttons.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between group">
          <div className="flex items-center gap-3 flex-1">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab hover:cursor-grabbing text-gray-400 hover:text-gray-600 p-1"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </div>
            <span className="flex-1 text-sm">{fact.text}</span>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onStartEdit(fact.id)}
              className="h-8 w-8 p-0"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(fact.id)}
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3,6 5,6 21,6" />
                <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
              </svg>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

interface WizardFactsListStepProps
  extends WizardStepProps<ComponentReturnTypeMap['FactsList']> {
  placeholder?: string;
  addButtonText?: string;
  emptyStateText?: string;
  maxFacts?: number;
  minFacts?: number;
  required?: boolean;
}

export const WizardFactsListStep: React.FC<WizardFactsListStepProps> = ({
  value,
  onChange,
  onNext,
  onPrevious: _onPrevious,
  error,
  disabled,
  placeholder: placeholderProp,
  addButtonText: addButtonTextProp,
  emptyStateText: emptyStateTextProp,
  maxFacts,
  minFacts = 0,
  required = false,
  setSubmitHandler,
}) => {
  const { t } = useTranslation('wizard');

  // Use translations as defaults
  const placeholder =
    placeholderProp ?? t('common.placeholders.enterFactPlaceholder');
  const addButtonText = addButtonTextProp ?? t('common.buttons.addFact');
  const emptyStateText = emptyStateTextProp ?? t('common.messages.noFactsYet');
  const [facts, setFacts] = useState<Fact[]>([]);
  const [newFactText, setNewFactText] = useState('');
  const [editingFactId, setEditingFactId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Parse initial value
  useEffect(() => {
    if (value && Array.isArray(value)) {
      setFacts(value);
    } else if (typeof value === 'string' && value) {
      // Handle case where value is a single string
      setFacts([{ id: '1', text: value }]);
    }
  }, [value]);

  // Update parent when facts change (but avoid infinite loops)
  // Don't auto-call onChange as it might trigger wizard navigation
  // Instead, only update on explicit user action (Next button)

  const commitPendingFact = (): Fact[] => {
    const trimmedText = newFactText.trim();
    if (!trimmedText || (maxFacts !== undefined && facts.length >= maxFacts)) {
      return facts;
    }
    const newFact: Fact = {
      id: Date.now().toString(),
      text: trimmedText,
    };
    const updatedFacts = [...facts, newFact];
    setFacts(updatedFacts);
    onChange(updatedFacts);
    setNewFactText('');
    return updatedFacts;
  };

  const addFact = () => {
    commitPendingFact();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.altKey)) {
      e.preventDefault();
      addFact();
    }
  };

  const handleEdit = (id: string, newText: string) => {
    setFacts((prev) => {
      const facts = prev.map((fact) =>
        fact.id === id ? { ...fact, text: newText } : fact
      );
      onChange(facts);
      return facts;
    });
    setEditingFactId(null);
  };

  const handleDelete = (id: string) => {
    setFacts((prev) => {
      const facts = prev.filter((fact) => fact.id !== id);
      onChange(facts);
      return facts;
    });
    setEditingFactId(null);
  };

  const handleStartEdit = (id: string) => {
    setEditingFactId(id);
  };

  const handleCancelEdit = () => {
    setEditingFactId(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setFacts((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        const facts = arrayMove(items, oldIndex, newIndex);
        onChange(facts);
        return facts;
      });
    }
  };

  const pendingFactCount = newFactText.trim() ? 1 : 0;
  const isFormValid = !required || facts.length + pendingFactCount >= minFacts;

  const handleNext = () => {
    const currentFacts = commitPendingFact();
    if (onNext) {
      onNext(currentFacts);
    }
  };

  // Register submit handler for the card footer's Next button
  const typedSetSubmitHandler = setSubmitHandler as
    | ((handler: (() => void) | null) => void)
    | undefined;
  typedSetSubmitHandler?.(handleNext);

  return (
    <div className="w-full mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('common.labels.factsList')}</span>
            <Badge variant="secondary">
              {facts.length}
              {maxFacts !== undefined && ` / ${maxFacts}`}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new fact input */}
          <div className="flex gap-4 items-center">
            <Textarea
              value={newFactText}
              onChange={(e) => setNewFactText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={
                disabled || (maxFacts !== undefined && facts.length >= maxFacts)
              }
              className="flex-1"
            />
            <Button
              onClick={addFact}
              disabled={
                disabled ||
                !newFactText.trim() ||
                (maxFacts !== undefined && facts.length >= maxFacts)
              }
            >
              {addButtonText}
            </Button>
          </div>

          {/* Facts list */}
          {facts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {emptyStateText}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={facts.map((fact) => fact.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {facts.map((fact) => (
                    <SortableFactItem
                      key={fact.id}
                      fact={fact}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      isEditing={editingFactId === fact.id}
                      onStartEdit={handleStartEdit}
                      onCancelEdit={handleCancelEdit}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {/* Validation messages */}
          {maxFacts !== undefined && facts.length >= maxFacts && (
            <p className="text-sm text-muted-foreground">
              {t('common.messages.maxFactsReached', { count: maxFacts })}
            </p>
          )}
          {required && facts.length < minFacts && (
            <p className="text-sm text-destructive">
              {t('common.messages.minFactsRequired', {
                count: minFacts,
                plural: minFacts !== 1 ? 's' : '',
              })}
            </p>
          )}
        </CardContent>
      </Card>

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {error}
        </div>
      )}

      {!typedSetSubmitHandler && (
        <div className="flex justify-end">
          <Button onClick={handleNext} disabled={disabled || !isFormValid}>
            {t('common.buttons.next')}
          </Button>
        </div>
      )}
    </div>
  );
};
